import { watch, isRef } from 'vue'
import type { Router } from 'vue-router'
import type { User } from '@/types'
import { logsApi } from '@/api/logs'

type AuthStoreLike = {
  user: { value: User | null } | User | null
  selectedExtension: { value: string | null } | string | null
}

type LogEvent = {
  ts: string
  type: string
  data?: Record<string, unknown>
}

const SESSION_ID_KEY = 'ui.sessionId'
let fallbackSessionId: string | null = null
let lastEnteredLogin: string | null = null
let flushTimer: number | null = null
let pendingEvents: LogEvent[] = []
const clientContext = {
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
  timeZone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined
}

function safeGetSessionStorageItem(key: string) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSetSessionStorageItem(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // ignore storage errors
  }
}

function safeGetLocalStorageItem(key: string) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSetLocalStorageItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore storage errors
  }
}

function getSessionId(): string {
  const existing = safeGetSessionStorageItem(SESSION_ID_KEY)
  if (existing) return existing
  if (fallbackSessionId) return fallbackSessionId
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`
  safeSetSessionStorageItem(SESSION_ID_KEY, id)
  fallbackSessionId = id
  return id
}

export function getSessionIdForReport(): string {
  return getSessionId()
}

function getLogKey(login: string, sessionId: string) {
  return `sessionLog.${login}.${sessionId}`
}

function appendLog(login: string, sessionId: string, event: LogEvent) {
  const key = getLogKey(login, sessionId)
  const current = safeGetLocalStorageItem(key)
  let logs: LogEvent[] = []
  if (current) {
    try {
      const parsed = JSON.parse(current)
      if (Array.isArray(parsed)) {
        logs = parsed as LogEvent[]
      }
    } catch {
      logs = []
    }
  }
  logs.push(event)
  safeSetLocalStorageItem(key, JSON.stringify(logs))
}

async function flushEvents(sessionId: string, getLogin: () => string) {
  if (pendingEvents.length === 0) return
  const eventsToSend = pendingEvents
  pendingEvents = []
  try {
    await logsApi.sendSessionLog({
      session_id: sessionId,
      login: getLogin(),
      entered_login: lastEnteredLogin || undefined,
      user_agent: clientContext.userAgent,
      locale: clientContext.locale,
      time_zone: clientContext.timeZone,
      events: eventsToSend
    })
  } catch {
    // swallow network errors; keep UI responsive
  }
}

function scheduleFlush(sessionId: string, getLogin: () => string) {
  if (flushTimer) return
  flushTimer = window.setTimeout(async () => {
    flushTimer = null
    await flushEvents(sessionId, getLogin)
  }, 1000)
}

function queueEvent(sessionId: string, getLogin: () => string, event: LogEvent) {
  pendingEvents.push(event)
  scheduleFlush(sessionId, getLogin)
}

export function logEnteredLogin(login: string) {
  lastEnteredLogin = login
}

export function exportSessionLog(login: string, sessionId?: string) {
  const id = sessionId || getSessionId()
  const key = getLogKey(login, id)
  const current = safeGetLocalStorageItem(key)
  if (!current) return

  const blob = new Blob([current], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `session-log-${login}-${id}.json`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function initSessionLogger(router: Router, authStore: AuthStoreLike) {
  const sessionId = getSessionId()
  const getUser = () => (isRef(authStore.user) ? authStore.user.value : authStore.user)
  const getExtension = () => (isRef(authStore.selectedExtension) ? authStore.selectedExtension.value : authStore.selectedExtension)
  const getLogin = () => getUser()?.login || 'anonymous'

  const startEvent: LogEvent = {
    ts: new Date().toISOString(),
    type: 'session_start',
    data: {
      userAgent: clientContext.userAgent,
      locale: clientContext.locale,
      timeZone: clientContext.timeZone,
      appVersion: import.meta.env.VITE_APP_VERSION || 'dev'
    }
  }
  appendLog(getLogin(), sessionId, startEvent)
  queueEvent(sessionId, getLogin, startEvent)

  let lastLogin = getLogin()

  watch(() => getUser()?.login, (login, prevLogin) => {
    const currentLogin = login || 'anonymous'
    lastLogin = currentLogin
    const event: LogEvent = {
      ts: new Date().toISOString(),
      type: login ? 'login' : 'logout',
      data: { previousLogin: prevLogin || null }
    }
    appendLog(currentLogin, sessionId, event)
    queueEvent(sessionId, getLogin, event)
  })

  watch(() => getExtension(), (value, prev) => {
    const event: LogEvent = {
      ts: new Date().toISOString(),
      type: 'extension_change',
      data: { from: prev || null, to: value || null }
    }
    appendLog(getLogin(), sessionId, event)
    queueEvent(sessionId, getLogin, event)
  })

  router.afterEach((to, from) => {
    const event: LogEvent = {
      ts: new Date().toISOString(),
      type: 'route_change',
      data: { from: from.fullPath, to: to.fullPath }
    }
    appendLog(getLogin(), sessionId, event)
    queueEvent(sessionId, getLogin, event)
  })

  window.addEventListener('beforeunload', () => {
    const event: LogEvent = {
      ts: new Date().toISOString(),
      type: 'session_end'
    }
    appendLog(lastLogin, sessionId, event)
    if (pendingEvents.length > 0 && navigator.sendBeacon) {
      const payload = {
        session_id: sessionId,
        login: getLogin(),
        entered_login: lastEnteredLogin || undefined,
        user_agent: clientContext.userAgent,
        locale: clientContext.locale,
        time_zone: clientContext.timeZone,
        events: [...pendingEvents, event]
      }
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
        navigator.sendBeacon('/api/v1/logs/session', blob)
        pendingEvents = []
      } catch {
        // ignore
      }
    }
  })

  return { sessionId }
}
